import { digestsApi } from '@/lib/api';
import type { FileDigestState } from '@mss_creator/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { digestFailureMessage } from './digest-messages';
import { DigestVersionCard } from './digest-version-card';

/**
 * One file's digest (level 1 of D9): derived from that file's extracted text
 * alone, which is what makes it individually regenerable without touching the
 * others.
 *
 * Owns its own mutations rather than taking callbacks from the panel: a
 * per-file pending state is the whole point of a synchronous regeneration —
 * regenerating one document must not grey out the other three.
 */

export interface FileDigestCardProps {
  matrixId: string;
  file: FileDigestState;
  /** True while a generation is in flight, or a digest job is running. */
  disabled: boolean;
  /** The matrix has no sujet/contexte — the model-calling button alone is blocked (issue #1). */
  framingMissing?: boolean;
  /**
   * This file's current digest predates the matrix's framing (issue #1) — it
   * read the document through a sujet that is no longer the matrix's.
   */
  stale?: boolean;
  /** Called before any model call, so the wizard's autosave debounce lands first. */
  onBeforeModelCall?: () => void;
}

/** "le 2 septembre à 14:31" — enough to place a digest against a framing change. */
function formatDigestDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function FileDigestCard({
  matrixId,
  file,
  disabled,
  framingMissing = false,
  stale = false,
  onBeforeModelCall,
}: FileDigestCardProps) {
  const queryClient = useQueryClient();

  /**
   * Both mutations invalidate the same two keys. `digest-state` because a new
   * file digest also changes `synthesisCurrent` (D13: freshness is set
   * equality over the current digest version ids, so any new version
   * invalidates the synthesis), and `matrix` because the backend stamps
   * `contextUpdatedAt`, which drives the per-cell staleness markers (D25).
   */
  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['digest-state', matrixId] });
    void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
  }

  const regenerateMutation = useMutation({
    mutationFn: (comment: string | null) => {
      onBeforeModelCall?.();
      return digestsApi.regenerateFileDigest(
        matrixId,
        file.contextFileId,
        comment === null ? {} : { comment },
      );
    },
    onSuccess: invalidate,
  });

  const editMutation = useMutation({
    mutationFn: (content: string) =>
      digestsApi.editFileDigest(matrixId, file.contextFileId, { content }),
    onSuccess: invalidate,
  });

  const failedMutation = regenerateMutation.isError
    ? regenerateMutation.error
    : editMutation.isError
      ? editMutation.error
      : null;

  const current = file.currentVersion;

  return (
    <DigestVersionCard
      title={file.filename}
      subject={`le digest de ${file.filename}`}
      version={current}
      versionCount={file.versionCount}
      badge={
        stale ? (
          <span className="rounded-md border border-warning bg-warning-tint px-2 py-0 font-semibold text-caption">
            À redérouler
          </span>
        ) : undefined
      }
      notice={
        stale && current ? (
          <p className="mt-2 text-caption text-gray-600">
            {`Produit le ${formatDigestDate(current.createdAt)}, sous le cadrage précédent.`}
            {/* Naming the hand-written case out loud: the one action offered
                here is exactly the one that would throw that work away. */}
            {current.origin === 'manual'
              ? ' Ce digest a été écrit à la main — un redéroulage remplacera votre texte.'
              : ''}
          </p>
        ) : undefined
      }
      emptyLabel="Ce document n’a pas encore de digest — il sera produit au lancement de la génération, ou dès maintenant si vous le préparez ici."
      editFieldLabel="Texte du digest"
      disabled={disabled}
      regenerateDisabled={framingMissing}
      regeneratePending={regenerateMutation.isPending}
      editPending={editMutation.isPending}
      error={failedMutation ? digestFailureMessage(failedMutation) : null}
      onRegenerate={(comment) => regenerateMutation.mutateAsync(comment)}
      onEdit={(content) => editMutation.mutateAsync(content)}
    />
  );
}
